import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import {
  ContentRefService,
  IChannelPageDetailContent,
  IShopChannelLivestream,
} from '@x/content/client';
import { ChannelContextService } from '@x/ecommerce-shop/app/channel/services/channel-context.service';
import {
  ChannelFetchActive,
  ChannelFetchAds,
  ChannelFetchAll,
  ChannelFetchContent,
  ChannelResetActiveChannelState,
  ChannelSetActive,
} from '@x/ecommerce-shop/app/channel/state/channel.actions';
import { ShopStorage } from '@x/ecommerce-shop/app/core/shop-storage/shop-storage.service';
import {
  IShopChannelDetail,
  IShopChannelItem,
  ShopAdService,
  ShopChannelService,
  TShopAd,
} from '@x/ecommerce/shop-client';
import { map, shareReplay, tap } from 'rxjs/operators';

export interface IChannelSelectOption {
  code: string;
  defaultCountry?: string | null;
  name: string;
  url?: string;
}

export interface IActiveChannel {
  channel: IShopChannelDetail;
  content?: IChannelPageDetailContent;
}

export interface ChannelStateModel {
  activeChannel: IActiveChannel | null;
  channels: IShopChannelItem[];
  ads: TShopAd[];
}

const defaults: ChannelStateModel = {
  activeChannel: null,
  channels: [],
  ads: [],
};

@State<ChannelStateModel>({
  name: 'channelState',
  defaults,
})
@Injectable()
export class ChannelState implements NgxsOnInit {
  constructor(
    private contentRefService: ContentRefService,
    private shopChannelService: ShopChannelService,
    private shopAdService: ShopAdService,
    private shopStorage: ShopStorage,
    private channelContext: ChannelContextService,
    @Inject(LOCALE_ID) private localeId: string,
  ) {}

  @Selector()
  static activeChannel(state: ChannelStateModel) {
    return state.activeChannel?.channel ?? null;
  }

  @Selector()
  static activeChannelContent(state: ChannelStateModel): IChannelPageDetailContent | undefined {
    return state.activeChannel?.content;
  }

  @Selector()
  static activeChannelCode(state: ChannelStateModel) {
    return state.activeChannel?.channel.code;
  }

  @Selector()
  static activeChannelDefaultCountry(state: ChannelStateModel) {
    return state.activeChannel?.channel.defaultCountry;
  }

  @Selector()
  static channels(state: ChannelStateModel) {
    return state.channels;
  }

  @Selector()
  static activeChannelContentLivestream(state: ChannelStateModel): IShopChannelLivestream | null {
    return state.activeChannel?.content?.livestream || null;
  }

  @Selector()
  static channelAds(state: ChannelStateModel): TShopAd[] {
    return state.ads;
  }

  @Selector()
  static defaultRegionId(state: ChannelStateModel) {
    return state.activeChannel?.channel.defaultRegion?.id;
  }

  @Action(ChannelFetchAll)
  fetchAll({ patchState }: StateContext<ChannelStateModel>) {
    return this.shopChannelService.fetchAllChannels(this.localeId).pipe(
      tap((channels) => {
        patchState({
          channels: [...channels],
        });
      }),
    );
  }

  @Action(ChannelFetchActive, { cancelUncompleted: true })
  fetchDetail(
    { dispatch }: StateContext<ChannelStateModel>,
    { activeChannelCode }: ChannelFetchActive,
  ) {
    const channelDetails$ = this.shopChannelService
      .fetchChannelDetail(activeChannelCode, this.localeId)
      .pipe(shareReplay());

    return channelDetails$.pipe(map((channel) => dispatch(new ChannelSetActive({ channel }))));
  }

  @Action(ChannelSetActive)
  setActive(
    { patchState, dispatch }: StateContext<ChannelStateModel>,
    { activeChannel }: ChannelSetActive,
  ) {
    this.shopStorage.channel = activeChannel.channel.code;
    patchState({ activeChannel });

    dispatch(new ChannelFetchAds(activeChannel.channel.code));
  }

  @Action(ChannelFetchAds, { cancelUncompleted: true })
  fetchAds(
    { patchState }: StateContext<ChannelStateModel>,
    { activeChannelCode }: ChannelFetchAds,
  ) {
    return this.shopAdService
      .fetchShopChannelAds$({
        channelCode: activeChannelCode,
        locale: this.localeId,
      })
      .pipe(
        tap((ads) => {
          patchState({ ads });
        }),
      );
  }

  @Action(ChannelFetchContent, { cancelUncompleted: true })
  fetchContent(
    { patchState, getState }: StateContext<ChannelStateModel>,
    { contentRef }: ChannelFetchContent,
  ) {
    const { activeChannel } = getState();

    if (!activeChannel) return;

    return this.contentRefService
      .fetchRef<IChannelPageDetailContent>(contentRef, this.localeId)
      .pipe(
        tap((content) => {
          patchState({
            activeChannel: {
              ...activeChannel,
              content,
            },
          });
        }),
      );
  }

  @Action(ChannelResetActiveChannelState)
  clearState({ patchState }: StateContext<ChannelStateModel>) {
    this.shopStorage.channel = null;
    const { activeChannel, ads } = defaults;

    patchState({ activeChannel, ads });
  }

  ngxsOnInit({ dispatch }: StateContext<any>): void {
    dispatch(new ChannelFetchAll());

    if (this.channelContext.channelCode) {
      dispatch(new ChannelFetchActive(this.channelContext.channelCode));
    }
  }
}
